import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import useAutomationId from '@/hooks/useAutomationId.ts';
import { AutomationEmailIdentity } from '@/types/integrations.ts';
import { AUTOMATION_EMAIL_IDENTITY_KEY } from '@/utils/queryKeys.ts';
import { get_automation_email_identity_url } from '@/utils/urls.ts';

export type UpdateAutomationEmailIdentityParams = Pick<
  AutomationEmailIdentity,
  'display_name_override' | 'reply_to_step_id'
>;

export const useRetrieveAutomationEmailIdentity = (enabled = true) => {
  const apiClient = useApiClient();
  const automationId = useAutomationId();
  return useQuery<AutomationEmailIdentity, AxiosError>({
    queryKey: AUTOMATION_EMAIL_IDENTITY_KEY(automationId),
    queryFn: async () => (await apiClient.get(get_automation_email_identity_url(automationId))).data,
    enabled: enabled && !!automationId,
  });
};

export const useUpdateAutomationEmailIdentity = () => {
  const apiClient = useApiClient();
  const automationId = useAutomationId();
  const queryClient = useQueryClient();
  return useMutation<AutomationEmailIdentity, AxiosError, UpdateAutomationEmailIdentityParams>({
    mutationFn: async (payload) =>
      (await apiClient.patch(get_automation_email_identity_url(automationId), payload)).data,
    onSuccess: (identity) => {
      queryClient.setQueryData(AUTOMATION_EMAIL_IDENTITY_KEY(automationId), identity);
    },
  });
};
